/**
 * A store for the management of UserNotificationS.
 *
 * File: \src\components\messages\UserNotificationStore.ts
 * Created Date: Thursday, April 28th 2022, 18:29:30
 * Author: Kircheva Boryana (HAU-EDCP)
 * -----
 * Last Modified: September 26th 2024, 16:03:57
 * Modified By: Boryana Kircheva (HAU-ED)
 * -----
 * Copyright (c) 2017-2022 Liebherr-Hausgeräte GmbH
 */
import Vue from 'vue';
import { isEqual } from 'lodash';
import { CubeLogger } from '@/Logger';

import UserNotification from './handler/UserNotification';
import UserSnack from './handler/UserSnack';
import UserWarning from './handler/UserWarning';
import UserError from './handler/UserError';
import { UserNotificationType } from './handler/UserNotificationType';
import InternalError from './handler/InternalError';
import { defineStore } from 'pinia';

interface UserNotificationStoreState {
  userSnacks: UserSnack[];
  userWarnings: UserWarning[];
  userErrors: UserError[];
  internalError: InternalError;
}

/**
 * A store for the management of UserNotificationS.
 * Store snacks, warnings and errors (for the user or internal).
 *
 * @export
 * @class UserNotificationStore
 */
export const useUserNotificationStore = defineStore('userNotificationStore', {
  state: (): UserNotificationStoreState => ({
    userSnacks: [],
    userWarnings: [],
    userErrors: [],
    internalError: undefined
  }),

  getters: {
    // /**
    //  * Getter for the snacks.
    //  *
    //  * @param {UserNotificationStoreState} state The state.
    //  * @returns {UserSnack[]} The snacks.
    //  */
    // getUserSnacks(): UserSnack[] {
    //   return this.userSnacks;
    // },

    // /**
    //  * Getter for the warnings.
    //  *
    //  * @param {UserNotificationStoreState} state The state.
    //  * @returns {UserWarning[]} The warnings.
    //  */
    // getUserWarnings(): UserWarning[] {
    //   return this.userWarnings;
    // },

    // /**
    //  * Getter for the errors.
    //  *
    //  * @param {UserNotificationStoreState} state The state.
    //  * @returns {UserError[]} The errors.
    //  */
    // getUserErrors(): UserError[] {
    //   return this.userErrors;
    // },

    // /**
    //  * Getter for the internal error. There can be only one!
    //  *
    //  * @param {UserNotificationStoreState} state
    //  * @returns {InternalError}
    //  */
    // getInternalError(): InternalError {
    //   return this.internalError;
    // },
    /**
     * Getter for all user notifications combined into one array.
     * The array is order:
     * - connection error (if there is one)
     * - internal error (if there is one)
     * - errors (if there are any)
     * - warnings (if there are any)
     * - snacks (if there are any)
     *
     * That way it is easy to check the contents for display ... you just needs to
     * grab the one on top.
     *
     * @returns {UserNotification[]}
     */
    getUserNotifications: function (): UserNotification[] {
      const logger = CubeLogger.$log.prefix('UserNotificationStore::getUserNotifications');

      const allNotifications = [];

      if (this.internalError) {
        allNotifications.push(this.internalError);
      }

      if (this.userErrors.length > 0) {
        allNotifications.push(...this.userErrors);
      }

      if (this.userWarnings.length > 0) {
        allNotifications.push(...this.userWarnings);
      }

      if (this.userSnacks.length > 0) {
        allNotifications.push(...this.userSnacks);
      }

      logger.debug('Getting notifications', { notifications: allNotifications });

      return allNotifications;
    }
  },

  actions: {
    /**
     * Pushes a user notification for display.
     *
     * @param {UserNotification} userNotification The user notification to push.
     */
    pushUserNotification(userNotification: UserNotification): void {
      const logger = CubeLogger.$log.prefix('UserNotificationStore::pushUserNotification');
      logger.debug('Pushing a notification', { notification: userNotification });

      if (userNotification.userNotificationType === UserNotificationType.SNACK) {
        this.addUserSnack(userNotification as UserSnack);
      }

      if (userNotification.userNotificationType === UserNotificationType.WARNING) {
        this.addUserWarning(userNotification as UserWarning);
      }

      if (userNotification.userNotificationType === UserNotificationType.ERROR) {
        this.addUserError(userNotification as UserError);
      }

      if (userNotification.userNotificationType === UserNotificationType.INTERNAL_ERROR) {
        this.setInternalError(userNotification as InternalError);
      }
    },

    /**
     * Adds a user snack. Pushes it to the back and ensure a maximum length of snacks.
     *
     * @param {UserSnack} snack The snack to push.
     */
    addUserSnack(snack: UserSnack): void {
      let storeSnacks = this.userSnacks;
      const SNACK_LIMIT: number = 3;

      const alreadyInStoreSnack = storeSnacks.find(storedSnack => isEqual(storedSnack, snack));

      if (!alreadyInStoreSnack) {
        storeSnacks.push(snack);

        if (storeSnacks.length > SNACK_LIMIT) {
          storeSnacks = storeSnacks.slice(1);
        }

        this.userSnacks = storeSnacks;
      }
    },

    /**
     * Adds a user warning. Pushes it to the back and ensure a maximum length of warnings.
     *
     * @param {UserWarning} warning The warning to push.
     */
    addUserWarning(warning: UserWarning): void {
      let storeWarnings = this.userWarnings;
      const WARNING_LIMIT: number = 3;

      const alreadyInStoreWarning = storeWarnings.find(storedWarning => isEqual(storedWarning, warning));

      if (!alreadyInStoreWarning) {
        storeWarnings.push(warning);

        if (storeWarnings.length > WARNING_LIMIT) {
          storeWarnings = storeWarnings.slice(1);
        }

        this.userWarnings = storeWarnings;
      }
    },

    /**
     * Adds a user error. Pushes it to the back and ensure a maximum length of errors.
     *
     * @param {UserError} error The error to push.
     */
    addUserError(error: UserError): void {
      let storeErrors = this.userErrors;
      const ERROR_LIMIT: number = 3;

      const alreadyInStoreError = storeErrors.find(storedError => isEqual(storedError, error));

      if (!alreadyInStoreError) {
        storeErrors.push(error);

        if (storeErrors.length > ERROR_LIMIT) {
          storeErrors = storeErrors.slice(1);
        }

        this.userErrors = storeErrors;
      }
    },

    /**
     * Sets the one internal error.
     *
     * @param {InternalError} error The one error.
     */
    setInternalError(error: InternalError): void {
      if (!this.internalError) {
        this.internalError = error;
      }
    },

    /**
     * Removes the user notification. Typically called after it was shown.
     *
     * @param {UserNotification} userNotification The notification to remove.
     */
    removeUserNotification(userNotification: UserNotification): void {
      if (userNotification) {
        if (userNotification.userNotificationType === UserNotificationType.SNACK) {
          let snacks: UserSnack[] = this.userSnacks as UserSnack[];

          snacks = snacks.filter((snack: UserSnack) => !isEqual(snack, userNotification));
          this.userSnacks = snacks;
        }

        if (userNotification.userNotificationType === UserNotificationType.WARNING) {
          let warnings: UserWarning[] = this.userWarnings as UserWarning[];

          warnings = warnings.filter((warning: UserWarning) => !isEqual(warning, userNotification));
          this.userWarnings = warnings;
        }

        if (userNotification.userNotificationType === UserNotificationType.ERROR) {
          let errors: UserError[] = this.userErrors as UserError[];

          errors = errors.filter((error: UserError) => !isEqual(error, userNotification));
          this.userErrors = errors;
        }

        if (userNotification.userNotificationType === UserNotificationType.INTERNAL_ERROR) {
          this.internalError = undefined;
        }
      }
    }
  }
});
